// src/scenes/ScoreboardScene.js
// 🏆 Enhanced ScoreboardScene with Robust Supabase Integration

import Phaser from 'phaser';
import { gameState } from '../game/state.js';
import { GAME_CONFIG } from '../game/config.js';

export default class ScoreboardScene extends Phaser.Scene {
  constructor() {
    super({ key: 'ScoreboardScene' });
    this.returnTo = 'MenuScene';
    this.gameComplete = false;
    this.scoresData = [];
    this.supabase = null;
    this.supabaseInitialized = false;
  }

  init(data) {
    this.returnTo = data.returnTo || 'MenuScene';
    this.gameComplete = data.gameComplete || false;
  }

  async create() {
    const { width, height } = this.scale;
    
    console.log('🏆 Enhanced Scoreboard Scene loaded');
    
    // Initialize Supabase first
    await this.initSupabase();
    
    // Enhanced vibrant background
    this.createEnhancedBackground();
    
    // Enhanced title
    this.createEnhancedTitle();
    
    if (this.gameComplete) {
      this.saveAndShowScore();
    } else {
      this.loadScoreboard();
    }
    
    this.createEnhancedBackButton();
  }

  async initSupabase() {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    console.log('🔗 Initializing Supabase...', { 
      hasUrl: !!supabaseUrl, 
      hasKey: !!supabaseKey 
    });
    
    if (supabaseUrl && supabaseKey) {
      try {
        // Try multiple import strategies
        let createClient;
        
        try {
          // Strategy 1: Standard import
          const supabaseModule = await import('@supabase/supabase-js');
          createClient = supabaseModule.createClient;
        } catch (error1) {
          console.warn('Strategy 1 failed, trying strategy 2:', error1);
          
          try {
            // Strategy 2: Destructured import
            const { createClient: createClientAlt } = await import('@supabase/supabase-js');
            createClient = createClientAlt;
          } catch (error2) {
            console.warn('Strategy 2 failed, trying CDN fallback:', error2);
            
            // Strategy 3: CDN fallback
            const supabaseCDN = await import('https://cdn.skypack.dev/@supabase/supabase-js');
            createClient = supabaseCDN.createClient;
          }
        }
        
        if (createClient) {
          this.supabase = createClient(supabaseUrl, supabaseKey);
          this.supabaseInitialized = true;
          console.log('✅ Supabase connected successfully');
          
          // Test connection
          const { data, error } = await this.supabase
            .from('wedding_scores')
            .select('count')
            .limit(1);
          
          if (error) {
            console.warn('⚠️ Supabase connection test failed:', error);
            this.supabase = null;
            this.supabaseInitialized = false;
          } else {
            console.log('✅ Supabase connection verified');
          }
        }
        
      } catch (error) {
        console.warn('⚠️ All Supabase connection strategies failed:', error);
        this.supabase = null;
        this.supabaseInitialized = false;
      }
    } else {
      console.warn('⚠️ Supabase environment variables not found');
      this.supabase = null;
      this.supabaseInitialized = false;
    }
  }

  createEnhancedBackground() {
    const { width, height } = this.scale;
    
    // Gradient background
    const bg = this.add.graphics();
    bg.fillGradientStyle(0xFF69B4, 0xFF69B4, 0x4ECDC4, 0x4ECDC4, 1);
    bg.fillRect(0, 0, width, height);
    
    // Add animated decorative clouds
    for (let i = 0; i < 8; i++) {
      const cloud = this.add.image(i * 150 + 50, 60 + Math.sin(i) * 20, 'cloud')
        .setScale(2 + Math.random())
        .setAlpha(0.6);
      
      this.tweens.add({
        targets: cloud,
        x: cloud.x + 30,
        duration: 8000 + (i * 500),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
    
    // Floating hearts and stars
    for (let i = 0; i < 12; i++) {
      const isHeart = Math.random() > 0.5;
      const decoration = this.add.image(
        Math.random() * width,
        Math.random() * height,
        isHeart ? 'heart' : 'star'
      ).setScale(1.5 + Math.random()).setAlpha(0.4);
      
      this.tweens.add({
        targets: decoration,
        y: decoration.y - 20,
        rotation: Math.PI * 2,
        duration: 4000 + Math.random() * 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }

  createEnhancedTitle() {
    const { width, height } = this.scale;
    
    // Main title with enhanced styling
    const title = this.add.text(width/2, height * 0.08, '🏆 WEDDING CHAMPIONS 🏆', {
      fontSize: '28px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 4,
      shadow: { offsetX: 3, offsetY: 3, color: '#000000', blur: 0, fill: true },
      align: 'center',
      wordWrap: { width: width * 0.9 }
    }).setOrigin(0.5);
    
    // Subtitle - dynamic based on connection
    const subtitleText = this.supabaseInitialized ? 
      'LIVE WEDDING LEADERBOARD' : 
      'LOCAL DEVICE SCORES';
    
    const subtitle = this.add.text(width/2, height * 0.15, subtitleText, {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    // Bouncing animation
    this.tweens.add({
      targets: [title, subtitle],
      scaleY: 1.05,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  async saveToSupabase(scoreData) {
    if (!this.supabase || !this.supabaseInitialized) {
      throw new Error('Supabase not initialized');
    }

    try {
      // Check if player already exists for this wedding
      const { data: existingScore, error: fetchError } = await this.supabase
        .from('wedding_scores')
        .select('*')
        .eq('name', scoreData.name)
        .eq('wedding_id', 'pablo-bettina-2024')
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      const dbData = {
        name: scoreData.name,
        character: scoreData.character,
        score: scoreData.score,
        level: gameState.level,
        level1: scoreData.level1,
        level2: scoreData.level2,
        level3: scoreData.level3,
        stats: scoreData.stats,
        achievements: scoreData.achievements,
        wedding_id: 'pablo-bettina-2024'
      };

      let result;
      if (existingScore && scoreData.score > existingScore.score) {
        const { data, error } = await this.supabase
          .from('wedding_scores')
          .update(dbData)
          .eq('name', scoreData.name)
          .eq('wedding_id', 'pablo-bettina-2024')
          .select();
        
        if (error) throw error;
        result = data[0];
        console.log(`📈 Updated high score for ${scoreData.name}`);
      } else if (!existingScore) {
        const { data, error } = await this.supabase
          .from('wedding_scores')
          .insert([dbData])
          .select();
        
        if (error) throw error;
        result = data[0];
        console.log(`🆕 Added new player: ${scoreData.name}`);
      } else {
        console.log(`📊 Score not higher than existing record for ${scoreData.name}`);
        result = existingScore;
      }

      return { success: true, data: result };
    } catch (error) {
      console.error('❌ Supabase save error:', error);
      throw error;
    }
  }

  async loadFromSupabase() {
    if (!this.supabase || !this.supabaseInitialized) {
      throw new Error('Supabase not initialized');
    }

    try {
      // Try leaderboard view first, fallback to direct table
      let data, error;
      
      try {
        const result = await this.supabase
          .from('wedding_leaderboard')
          .select('*')
          .limit(20);
        data = result.data;
        error = result.error;
      } catch (viewError) {
        console.warn('Leaderboard view not available, using direct table access');
        const result = await this.supabase
          .from('wedding_scores')
          .select('*')
          .eq('wedding_id', 'pablo-bettina-2024')
          .order('score', { ascending: false })
          .limit(20);
        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      return data.map((row, index) => ({
        name: row.name,
        character: row.character,
        score: row.score,
        level1: row.level1,
        level2: row.level2,
        level3: row.level3,
        stats: row.stats || {},
        achievements: row.achievements || {},
        timestamp: row.timestamp || row.created_at,
        rank: row.rank || (index + 1)
      }));
    } catch (error) {
      console.error('❌ Supabase load error:', error);
      throw error;
    }
  }

  async saveAndShowScore() {
    const { width, height } = this.scale;
    
    const savingContainer = this.add.container(width/2, height * 0.4);
    
    const savingBg = this.add.graphics();
    savingBg.fillStyle(0x000000, 0.8);
    savingBg.fillRoundedRect(-150, -40, 300, 80, 10);
    savingContainer.add(savingBg);
    
    const savingText = this.add.text(0, -10, 
      this.supabaseInitialized ? 'SAVING TO DATABASE...' : 'SAVING SCORE...', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    savingContainer.add(savingText);
    
    const spinner = this.add.text(0, 15, '⏳', {
      fontSize: '20px'
    }).setOrigin(0.5);
    savingContainer.add(spinner);
    
    this.tweens.add({
      targets: spinner,
      rotation: Math.PI * 2,
      duration: 1000,
      repeat: -1
    });
    
    const scoreData = {
      name: gameState.playerName,
      character: gameState.character,
      score: Math.floor(gameState.score),
      level1: Math.floor(gameState.levelScores[1] || 0),
      level2: Math.floor(gameState.levelScores[2] || 0),
      level3: Math.floor(gameState.levelScores[3] || 0),
      stats: {
        itemsCollected: gameState.stats.itemsCollected,
        obstaclesHit: gameState.stats.obstaclesHit,
        npcsHelped: gameState.stats.npcsHelped,
        bestCombo: gameState.stats.bestCombo,
        perfectLevels: gameState.stats.perfectLevels,
        playTime: Math.floor(gameState.stats.playTime / 1000)
      },
      achievements: { ...gameState.achievements },
      timestamp: new Date().toISOString(),
      gameVersion: GAME_CONFIG.VERSION
    };
    
    try {
      if (this.supabaseInitialized) {
        await this.saveToSupabase(scoreData);
        savingText.setText('SAVED TO DATABASE!');
        savingText.setStyle({ color: '#32CD32' });
      } else {
        this.saveScoreLocally(scoreData);
        savingText.setText('SAVED LOCALLY!');
        savingText.setStyle({ color: '#FFA500' });
      }
    } catch (error) {
      console.warn('⚠️ Database save failed, saving locally');
      this.saveScoreLocally(scoreData);
      savingText.setText('SAVED LOCALLY!');
      savingText.setStyle({ color: '#FFA500' });
    }
    
    this.time.delayedCall(1500, () => {
      savingContainer.destroy();
      this.loadScoreboard();
    });
  }

  saveScoreLocally(scoreData) {
    try {
      const localScores = JSON.parse(localStorage.getItem('wedding-game-local-scores') || '[]');
      
      const existingPlayerIndex = localScores.findIndex(score => 
        score.name.toLowerCase() === scoreData.name.toLowerCase()
      );
      
      if (existingPlayerIndex !== -1) {
        if (scoreData.score > localScores[existingPlayerIndex].score) {
          localScores[existingPlayerIndex] = scoreData;
          console.log(`📈 Updated local high score for ${scoreData.name}`);
        }
      } else {
        localScores.push(scoreData);
        console.log(`🆕 Added new local player: ${scoreData.name}`);
      }
      
      localScores.sort((a, b) => b.score - a.score);
      localScores.splice(20);
      
      localStorage.setItem('wedding-game-local-scores', JSON.stringify(localScores));
    } catch (error) {
      console.warn('Could not save score locally:', error);
    }
  }

  async loadScoreboard() {
    const { width, height } = this.scale;
    
    const loadingContainer = this.add.container(width/2, height * 0.5);
    
    const loadingBg = this.add.graphics();
    loadingBg.fillStyle(0x000000, 0.8);
    loadingBg.fillRoundedRect(-180, -50, 360, 100, 10);
    loadingContainer.add(loadingBg);
    
    const loadingText = this.add.text(0, -15, 
      this.supabaseInitialized ? 'LOADING FROM DATABASE...' : 'LOADING SCORES...', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#4ECDC4',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    loadingContainer.add(loadingText);
    
    const loadingBarBg = this.add.rectangle(0, 15, 200, 10, 0x333333);
    const loadingBar = this.add.rectangle(-95, 15, 10, 8, 0x32CD32);
    loadingContainer.add([loadingBarBg, loadingBar]);
    
    this.tweens.add({
      targets: loadingBar,
      width: 190,
      duration: 1500,
      ease: 'Power2'
    });
    
    try {
      if (this.supabaseInitialized) {
        const scores = await this.loadFromSupabase();
        this.scoresData = scores;
        
        loadingContainer.destroy();
        this.displayEnhancedScoreboard(scores, false);
        console.log('📊 Loaded scores from database');
      } else {
        throw new Error('Supabase not available');
      }
    } catch (error) {
      console.warn('⚠️ Could not load from database, using local scores');
      loadingContainer.destroy();
      this.displayLocalScores();
    }
  }

  displayLocalScores() {
    try {
      const localScores = JSON.parse(localStorage.getItem('wedding-game-local-scores') || '[]');
      this.displayEnhancedScoreboard(localScores, true);
      console.log('📊 Loaded local scores');
    } catch (error) {
      this.displayNoScores();
    }
  }

  // Keep all the existing display methods unchanged...
  displayEnhancedScoreboard(scores, isLocal = false) {
    const { width, height } = this.scale;
    
    if (!scores || scores.length === 0) {
      this.displayNoScores();
      return;
    }
    
    const scoreboardContainer = this.add.container(0, 0);
    
    const mainBg = this.add.graphics();
    mainBg.fillGradientStyle(0xE0E0E0, 0xE0E0E0, 0xF5F5F5, 0xF5F5F5, 0.95);
    mainBg.fillRoundedRect(width * 0.05, height * 0.22, width * 0.9, height * 0.6, 12);
    mainBg.lineStyle(4, 0xFFD700);
    mainBg.strokeRoundedRect(width * 0.05, height * 0.22, width * 0.9, height * 0.6, 12);
    scoreboardContainer.add(mainBg);
    
    const headerBg = this.add.graphics();
    headerBg.fillStyle(0x2C3E50);
    headerBg.fillRoundedRect(width * 0.08, height * 0.25, width * 0.84, 25, 8);
    scoreboardContainer.add(headerBg);
    
    const headers = [
      { text: 'RANK', x: 0.12, color: '#FFD700' },
      { text: 'PLAYER', x: 0.25, color: '#FFD700' },
      { text: 'CHARACTER', x: 0.45, color: '#FFD700' },
      { text: 'SCORE', x: 0.65, color: '#FFD700' },
      { text: 'DATE', x: 0.8, color: '#FFD700' }
    ];
    
    headers.forEach(header => {
      const headerText = this.add.text(width * header.x, height * 0.268, header.text, {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: header.color,
        stroke: '#000000',
        strokeThickness: 1
      }).setOrigin(0.5);
      scoreboardContainer.add(headerText);
    });
    
    const maxEntries = Math.min(scores.length, 10);
    
    for (let i = 0; i < maxEntries; i++) {
      const score = scores[i];
      const y = height * 0.32 + (i * 32);
      const rank = score.rank || (i + 1);
      
      if (i % 2 === 1) {
        const rowBg = this.add.graphics();
        rowBg.fillStyle(0xF0F0F0, 0.5);
        rowBg.fillRoundedRect(width * 0.08, y - 12, width * 0.84, 28, 4);
        scoreboardContainer.add(rowBg);
      }
      
      let rankDisplay, rankColor;
      switch (rank) {
        case 1:
          rankDisplay = '👑 1st';
          rankColor = '#FFD700';
          break;
        case 2:
          rankDisplay = '🥈 2nd';
          rankColor = '#C0C0C0';
          break;
        case 3:
          rankDisplay = '🥉 3rd';
          rankColor = '#CD7F32';
          break;
        default:
          rankDisplay = `${rank}${this.getOrdinalSuffix(rank)}`;
          rankColor = '#2C3E50';
      }
      
      const rankText = this.add.text(width * 0.12, y, rankDisplay, {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: rankColor,
        stroke: '#FFFFFF',
        strokeThickness: 1
      }).setOrigin(0.5);
      scoreboardContainer.add(rankText);
      
      const playerName = score.name.length > 12 ? 
        score.name.substring(0, 12) + '...' : score.name;
      
      const nameText = this.add.text(width * 0.25, y, playerName, {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#2C3E50',
        stroke: '#FFFFFF',
        strokeThickness: 1
      }).setOrigin(0.5);
      scoreboardContainer.add(nameText);
      
      const characterInfo = this.getCharacterDisplay(score.character);
      const characterText = this.add.text(width * 0.45, y, characterInfo.emoji, {
        fontSize: '16px'
      }).setOrigin(0.5);
      scoreboardContainer.add(characterText);
      
      const scoreText = this.add.text(width * 0.65, y, score.score.toLocaleString(), {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#1E90FF',
        stroke: '#FFFFFF',
        strokeThickness: 1
      }).setOrigin(0.5);
      scoreboardContainer.add(scoreText);
      
      const date = new Date(score.timestamp);
      const dateText = this.add.text(width * 0.8, y, 
        `${date.getDate()}/${date.getMonth() + 1}`, {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#666666'
      }).setOrigin(0.5);
      scoreboardContainer.add(dateText);
      
      if (this.gameComplete && 
          score.name.toLowerCase() === gameState.playerName.toLowerCase() && 
          Math.abs(score.score - gameState.score) < 10) {
        
        const highlightBg = this.add.graphics();
        highlightBg.fillStyle(0xFFD700, 0.3);
        highlightBg.fillRoundedRect(width * 0.08, y - 12, width * 0.84, 28, 4);
        highlightBg.lineStyle(2, 0xFFD700);
        highlightBg.strokeRoundedRect(width * 0.08, y - 12, width * 0.84, 28, 4);
        scoreboardContainer.add(highlightBg);
        
        if (rank === 1) {
          const newRecordText = this.add.text(width * 0.92, y, '🆕 NEW RECORD!', {
            fontSize: '12px',
            fontFamily: 'Arial, sans-serif',
            color: '#FF1493',
            stroke: '#FFFFFF',
            strokeThickness: 1
          }).setOrigin(0.5);
          scoreboardContainer.add(newRecordText);
          
          this.tweens.add({
            targets: newRecordText,
            scaleX: 1.1,
            scaleY: 1.1,
            duration: 1000,
            yoyo: true,
            repeat: -1
          });
        }
        
        scoreboardContainer.bringToTop(rankText);
        scoreboardContainer.bringToTop(nameText);
        scoreboardContainer.bringToTop(characterText);
        scoreboardContainer.bringToTop(scoreText);
        scoreboardContainer.bringToTop(dateText);
      }
    }
    
    const footerY = height * 0.85;
    
    if (isLocal) {
      const offlineText = this.add.text(width/2, footerY, '📱 LOCAL DEVICE SCORES', {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#FFA500',
        stroke: '#000000',
        strokeThickness: 1
      }).setOrigin(0.5);
      scoreboardContainer.add(offlineText);
    } else {
      const onlineText = this.add.text(width/2, footerY, '🌐 LIVE DATABASE', {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#32CD32',
        stroke: '#000000',
        strokeThickness: 1
      }).setOrigin(0.5);
      scoreboardContainer.add(onlineText);
    }
    
    const competitionText = this.add.text(width/2, footerY + 15, 
      `Total Players: ${scores.length} • Updated: ${new Date().toLocaleDateString()}`, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#666666'
    }).setOrigin(0.5);
    scoreboardContainer.add(competitionText);
  }

  getCharacterDisplay(character) {
    const info = {
      'BRIDE': { emoji: '👰', name: 'Bettina' },
      'GROOM': { emoji: '🤵', name: 'Pablo' }
    };
    return info[character] || info['BRIDE'];
  }

  getOrdinalSuffix(num) {
    const j = num % 10;
    const k = num % 100;
    if (j == 1 && k != 11) return 'st';
    if (j == 2 && k != 12) return 'nd';
    if (j == 3 && k != 13) return 'rd';
    return 'th';
  }

  displayNoScores() {
    const { width, height } = this.scale;
    
    const noScoresContainer = this.add.container(width/2, height * 0.5);
    
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.8);
    bg.fillRoundedRect(-200, -100, 400, 200, 15);
    bg.lineStyle(3, 0xFFD700);
    bg.strokeRoundedRect(-200, -100, 400, 200, 15);
    noScoresContainer.add(bg);
    
    const noScoresText = this.add.text(0, -30, 'NO SCORES YET!', {
      fontSize: '20px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    noScoresContainer.add(noScoresText);
    
    const encourageText = this.add.text(0, 10, 'BE THE FIRST WEDDING GUEST\nTO SET A HIGH SCORE!', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 1,
      align: 'center'
    }).setOrigin(0.5);
    noScoresContainer.add(encourageText);
    
    const emojiText = this.add.text(0, 60, '🎉 🎮 💒 🎉', {
      fontSize: '24px'
    }).setOrigin(0.5);
    noScoresContainer.add(emojiText);
    
    this.tweens.add({
      targets: emojiText,
      y: 50,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  createEnhancedBackButton() {
    const { width, height } = this.scale;
    
    const buttonText = this.gameComplete ? 'PLAY AGAIN' : 'BACK TO MENU';
    const buttonColor = this.gameComplete ? 0x32CD32 : 0xFF6347;
    
    const buttonContainer = this.add.container(width/2, height * 0.92);
    
    const buttonBg = this.add.graphics();
    buttonBg.fillStyle(buttonColor);
    buttonBg.fillRoundedRect(-100, -20, 200, 40, 10);
    buttonBg.lineStyle(4, 0xFFD700);
    buttonBg.strokeRoundedRect(-100, -20, 200, 40, 10);
    
    const buttonGlow = this.add.graphics();
    buttonGlow.fillStyle(buttonColor, 0.3);
    buttonGlow.fillRoundedRect(-105, -25, 210, 50, 12);
    
    const buttonTextObj = this.add.text(0, 0, buttonText, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    buttonContainer.add([buttonGlow, buttonBg, buttonTextObj]);
    
    const hitArea = this.add.rectangle(width/2, height * 0.92, 200, 40, 0x000000, 0)
      .setInteractive()
      .on('pointerdown', () => this.goBack())
      .on('pointerover', () => {
        this.tweens.add({
          targets: buttonContainer,
          scaleX: 1.05,
          scaleY: 1.05,
          duration: 200
        });
        buttonBg.clear();
        buttonBg.fillStyle(buttonColor, 0.8);
        buttonBg.fillRoundedRect(-100, -20, 200, 40, 10);
        buttonBg.lineStyle(4, 0xFFD700);
        buttonBg.strokeRoundedRect(-100, -20, 200, 40, 10);
      })
      .on('pointerout', () => {
        this.tweens.add({
          targets: buttonContainer,
          scaleX: 1,
          scaleY: 1,
          duration: 200
        });
        buttonBg.clear();
        buttonBg.fillStyle(buttonColor);
        buttonBg.fillRoundedRect(-100, -20, 200, 40, 10);
        buttonBg.lineStyle(4, 0xFFD700);
        buttonBg.strokeRoundedRect(-100, -20, 200, 40, 10);
      });
  }

  goBack() {
    console.log(`🔙 Returning to ${this.returnTo}`);
    
    if (this.gameComplete) {
      gameState.reset();
    }
    
    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(this.returnTo);
    });
  }
}